import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Ticket } from './models/ticket.model';

import * as ACTIONS from './+state/account-tickets.actions';
import * as SELECTORS from './+state/account-tickets.selectors';
import { AccountTicketsService } from './services/account-tickets.service';

@Injectable()
export class AccountTicketsNgrxFacade {
    combinedTickets$: Observable<Ticket[]> = this.store.select(SELECTORS.getCombinedAllTickets);

    accountTickets$: Observable<Ticket[]> = this.store.select(SELECTORS.getAllAccountTickets);
    loaded$: Observable<boolean> = this.store.select(SELECTORS.ticketsLoaded);
    allPagesLoaded$: Observable<boolean> = this.store.select(SELECTORS.allPagesLoaded);
    offlinePagesLoaded$: Observable<boolean> = this.store.select(SELECTORS.offflinePagesLoaded);

    deviceTickets$: Observable<Ticket[]> = this.store.select(SELECTORS.getDeviceTickets);
    deviceTicketsLoaded$: Observable<boolean> = this.store.select(SELECTORS.deviceTicketsLoaded);

    page$: Observable<number> = this.store.select(SELECTORS.getPage);
    latestLoadedPage$: Observable<number> = this.store.select(SELECTORS.latestLoadedPage);

    shouldLoadPage$: Observable<number> = this.store.select(SELECTORS.shouldLoadPage);

    getTicket$ = (ticketId: number | string) => this.store.select(SELECTORS.getTicket(ticketId));

    constructor(
        private store: Store,
        private accountTicketService: AccountTicketsService
    ) {}

    fetchAccountTickets(expiredHours, expiredMinutes): void {
        this.store.dispatch(ACTIONS.fetchAllAccountTickets({ expiredHours, expiredMinutes }));
    }

    fetchPaginatedAccountTickets({
        limit,
        page,
        expiredHours,
        expiredMinutes
    }: {
        limit: number;
        page: number;
        expiredHours: number;
        expiredMinutes: number;
    }): void {
        this.store.dispatch(ACTIONS.fetchPaginatedAccountTickets({ limit, page, expiredHours, expiredMinutes }));
    }

    refetchAllAccountTicketPages({
        limit,
        expiredHours,
        expiredMinutes
    }: {
        limit: number;
        expiredHours: number;
        expiredMinutes: number;
    }): void {
        this.store.dispatch(ACTIONS.refetchAllAccountTicketPages({ limit, expiredHours, expiredMinutes }));
    }

    fetchDeviceTickets({ expiredHours, expiredMinutes }: { expiredHours: number; expiredMinutes: number }): void {
        this.store.dispatch(ACTIONS.fetchDeviceTickets({ expiredHours, expiredMinutes }));
    }

    fetchAccountTicketsHistory(
        formattedFromDate: string,
        formattedToDate: string,
        includeReturnedTickets: boolean
    ): void {
        this.store.dispatch(
            ACTIONS.fetchAccountTicketsHistory({ formattedFromDate, formattedToDate, includeReturnedTickets })
        );
    }

    activateTicket(docNum: string): void {
        this.store.dispatch(ACTIONS.activateTicket({ docNum }));
    }

    preActivateTicketCheck(docNum: string) {
        return this.accountTicketService.preActivateTicketCheck(docNum);
    }

    reactivateTicket(docNum: string): void {
        this.store.dispatch(ACTIONS.reactivateTicket({ docNum }));
    }

    fetchAccountTicketById(ticketId) {
        return this.accountTicketService.fetchAccountTicketById(ticketId);
    }

    resetAccountTickets() {
        this.store.dispatch(ACTIONS.resetAccountTickets());
    }

    incrementPageIndex(incrementBy: number) {
        this.store.dispatch(ACTIONS.incrementPageIndex({ incrementBy }));
    }

    resetState(): void {
        this.store.dispatch(ACTIONS.resetState());
    }
}
